'use client'

import { cloneElement, type ReactElement, useEffect, useState } from 'react'

import cn from 'clsx'
import { Heading } from '@/components/Text/Heading'

import css from './side-bar-category.module.css'
import { Box, type BoxProps } from '@/components/Box/Box'
import { Hairline } from '@/components/Hairline/Hairline'
import { HyperLink } from '@/components/hyper-link/hyper-link'
import { SidebarNavItem } from '@/components/SidebarNavItem/SidebarNavItem'
import { ChevronDown } from '@doist/icons/brand'

export interface SideBarCategoryProps extends Omit<BoxProps, 'onChange'> {
    name: string
    icon: ReactElement
    links?: string[]
    linksType?: 'button' | 'a'
    activeLinkIndex?: number
    disabled?: boolean
    onChange?: (value: number) => void
    expanded?: boolean
    onCategoryClick: () => void
}

export function SideBarCategory({
    name,
    icon,
    links = [],
    linksType = 'a',
    expanded = false,
    activeLinkIndex = 0,
    onCategoryClick = () => null,
    onChange,
    disabled,
    ...rest
}: SideBarCategoryProps) {
    const ClonedIcon = cloneElement(icon, {
        width: 16,
        height: 16,
        color: 'var(--selectable-selected-background-idle)',
    })
    const [internalSelectedIndex, setInternalSelectedIndex] = useState(activeLinkIndex)

    useEffect(() => {
        setInternalSelectedIndex(activeLinkIndex)
    }, [activeLinkIndex])

    const onSelectLinkIndex = (newIndex: number) => {
        setInternalSelectedIndex(newIndex)
        if (onChange) onChange(newIndex)
    }

    return (
        <Box {...rest} className={css.wrapper}>
            <HyperLink
                tag="button"
                disabled={disabled}
                onClick={() => !disabled && onCategoryClick()}
                className={cn(
                    css.categoryButton,
                    { [css.expanded]: expanded },
                    { [css.disabled]: disabled },
                )}
            >
                <Box className={css.categoryTitle}>
                    {ClonedIcon}
                    <Heading level="3" size="minor-base" weight="semibold">
                        {name}
                    </Heading>
                </Box>
                <ChevronDown width={18} height={18} className={css.chevron} />
            </HyperLink>

            {expanded && links.length > 0 && (
                <>
                    <Box className={css.linksContainer}>
                        <ul>
                            {links.map((link, idx) => (
                                <SidebarNavItem
                                    tag={linksType}
                                    key={link}
                                    active={internalSelectedIndex === idx}
                                    onClick={() => onSelectLinkIndex(idx)}
                                >
                                    {link}
                                </SidebarNavItem>
                            ))}
                        </ul>
                    </Box>
                    <Hairline spacing={16} />
                </>
            )}
        </Box>
    )
}
